import React, { useMemo, useCallback, useEffect } from 'react'
import {
  Land,
  Zoning,
  ZoningType,
  ZoningReportData,
  ZoningEstimateStats,
} from '@popety-io/types'

import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { Icon, Loading } from '../../atoms'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import { useMapSectionOptional } from '../../molecules/AnalysisMap/context'
import { useZoningData } from './hooks/useZoningData'
import { useZonePrioritization } from './hooks/useZonePrioritization'
import { ZONING_TYPE_ICONS, ZONING_TYPE_COLORS } from './utils/zoningConstants'
import {
  ZoningSectionTranslations,
  DEFAULT_ZONING_TRANSLATIONS,
} from './ZoningSection.types'
import type { ZoningDataByType } from './utils/zoningHelpers'
import { ZoneCard } from './components/ZoneCard'
import type { ZoneQAService, ZoneQATranslations } from './components/ZoneQA'
import {
  Container,
  ContentContainer,
  TabSubtitle,
  TabSubtitleHeader,
  TabSubtitleBadge,
  TabSubtitleDescription,
  ZonesContainer,
  EmptyState,
  ErrorState,
  LoadingState,
} from './ZoningSection.styles'

/**
 * Helper function to determine score variant based on value
 */
const getScoreVariant = (score: number): 'danger' | 'warning' | 'success' => {
  if (score >= 67) return 'success'
  if (score >= 34) return 'warning'
  return 'danger'
}

export interface ZoningSectionProps {
  land?: Land
  underExploitedScore?: number
  getZonesByIds: (type: ZoningType, ids: string[]) => Promise<Zoning[]>
  landData?: {
    std?: number
    sbd?: number
    sb?: number
    spd?: number
    sbpu?: number
    sp?: number
    vbr?: number
    ios?: number
    cos?: number
    ius?: number
    cus?: number
    ibus?: number
    mass_index?: number
    max_height?: number
    max_floor_nb?: number
    unit_nb?: number
  }
  landId?: string
  zoningOverridesService?: any // Will be typed from the service
  zoningService?: any // Service for fetching documents
  /** Optional callback to receive zoning data updates (for app-level context integration) */
  onDataChange?: (data: ZoningDataByType) => void
  /** Optional translations for internationalization */
  translations?: ZoningSectionTranslations
  /** Optional locale for date formatting (e.g., 'en', 'fr', 'de', 'it') */
  locale?: string
  /** Read-only mode - hides edit buttons and disables inputs (for public shares) */
  readOnly?: boolean
  /** Service for Zone Q&A - enables Q&A panel for general (communal) zones */
  zoneQAService?: ZoneQAService
  /** Translations for Zone Q&A panel */
  zoneQATranslations?: ZoneQATranslations
  /** Callback to transcribe voice audio for Zone Q&A */
  onTranscribeVoice?: (audioBlob: Blob) => Promise<string>
  /** Callback to report an incorrect metric value */
  onReportIncorrect?: (data: ZoningReportData) => Promise<void>
  /** Callback to fetch estimate stats for ALG justification panel */
  onFetchEstimateStats?: (
    zoneType: string,
    zoneId: string,
    metricKey: string
  ) => Promise<ZoningEstimateStats | null>
  /** Callback when the effective distance_limit value changes in any zone (for map buffer) */
  onDistanceLimitChange?: (value: number | undefined) => void
}

export const ZoningSection: React.FC<ZoningSectionProps> = ({
  land,
  underExploitedScore,
  getZonesByIds,
  landData,
  landId,
  zoningOverridesService,
  zoningService,
  onDataChange,
  translations,
  locale,
  readOnly = false,
  zoneQAService,
  zoneQATranslations,
  onTranscribeVoice,
  onReportIncorrect,
  onFetchEstimateStats,
  onDistanceLimitChange,
}) => {
  // Merge translations with defaults
  const t = useMemo(
    () => ({
      ...DEFAULT_ZONING_TRANSLATIONS,
      ...translations,
      zoneTypes: {
        ...DEFAULT_ZONING_TRANSLATIONS.zoneTypes,
        ...translations?.zoneTypes,
      },
      zonePriorityLabels: {
        ...DEFAULT_ZONING_TRANSLATIONS.zonePriorityLabels,
        ...translations?.zonePriorityLabels,
      },
      zoneDescriptions: {
        ...DEFAULT_ZONING_TRANSLATIONS.zoneDescriptions,
        ...translations?.zoneDescriptions,
      },
      metricsTable: {
        ...DEFAULT_ZONING_TRANSLATIONS.metricsTable,
        ...translations?.metricsTable,
        headers: {
          ...DEFAULT_ZONING_TRANSLATIONS.metricsTable?.headers,
          ...translations?.metricsTable?.headers,
        },
        groups: {
          ...DEFAULT_ZONING_TRANSLATIONS.metricsTable?.groups,
          ...translations?.metricsTable?.groups,
        },
        derivedSurfaces: {
          ...DEFAULT_ZONING_TRANSLATIONS.metricsTable?.derivedSurfaces,
          ...translations?.metricsTable?.derivedSurfaces,
        },
      },
      sourceLabels: {
        ...DEFAULT_ZONING_TRANSLATIONS.sourceLabels,
        ...translations?.sourceLabels,
      },
      status: {
        ...DEFAULT_ZONING_TRANSLATIONS.status,
        ...translations?.status,
      },
      header: {
        ...DEFAULT_ZONING_TRANSLATIONS.header,
        ...translations?.header,
      },
      documents: {
        ...DEFAULT_ZONING_TRANSLATIONS.documents,
        ...translations?.documents,
        sourceLabels: {
          ...DEFAULT_ZONING_TRANSLATIONS.documents?.sourceLabels,
          ...translations?.documents?.sourceLabels,
        },
        sourceTitles: {
          ...DEFAULT_ZONING_TRANSLATIONS.documents?.sourceTitles,
          ...translations?.documents?.sourceTitles,
        },
      },
      legend: {
        ...DEFAULT_ZONING_TRANSLATIONS.legend,
        ...translations?.legend,
        sources: {
          ...DEFAULT_ZONING_TRANSLATIONS.legend?.sources,
          ...translations?.legend?.sources,
        },
        agreement: {
          ...DEFAULT_ZONING_TRANSLATIONS.legend?.agreement,
          ...translations?.legend?.agreement,
        },
        difference: {
          ...DEFAULT_ZONING_TRANSLATIONS.legend?.difference,
          ...translations?.legend?.difference,
        },
      },
    }),
    [translations]
  )
  // Fetch zoning data
  const { data, coverageByZoneId, loading, error } = useZoningData({
    land,
    getZonesByIds,
    enabled: !!land,
  })

  // Manage tab prioritization
  const { allTabs, availableTabs, activeTab, setActiveTab } =
    useZonePrioritization(data)

  // Get zones for active tab
  const activeZones = data && activeTab ? data[activeTab] : []

  // Get map section context for syncing disabled state and tab selection
  // (optional - only available when used within AnalysisMap)
  const mapContext = useMapSectionOptional()

  // Propagate disabled zoning types to map context (if available)
  useEffect(() => {
    if (mapContext?.setDisabledZoningTypes) {
      const disabled = allTabs
        .filter((tab) => tab.disabled)
        .map((tab) => tab.type)
      mapContext.setDisabledZoningTypes(disabled)
    }
  }, [allTabs, mapContext])

  // Sync active tab when map filter changes externally (if context available)
  useEffect(() => {
    if (
      mapContext?.selectedZoningType &&
      mapContext.selectedZoningType !== 'all' &&
      mapContext.selectedZoningType !== activeTab
    ) {
      setActiveTab(mapContext.selectedZoningType)
    }
  }, [mapContext?.selectedZoningType, activeTab, setActiveTab])

  // Pass zoning data to map context when available (library context)
  useEffect(() => {
    if (mapContext?.setSectionData && data) {
      mapContext.setSectionData({
        zoningData: data,
      })
    }
  }, [data, mapContext])

  // Call onDataChange callback when data changes (for app-level context integration)
  useEffect(() => {
    if (onDataChange && data) {
      onDataChange(data)
    }
  }, [data, onDataChange])

  // Handle tab change
  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId as ZoningType)
      // Update map context if available
      mapContext?.setSelectedZoningType?.(tabId as ZoningType)
    },
    [setActiveTab, mapContext]
  )

  // Create tabs data for AnalysisTabButtonMenu
  const tabs = useMemo(
    () =>
      allTabs.map((tab) => ({
        id: tab.type,
        label: t.zoneTypes?.[tab.type] || tab.type,
        icon: ZONING_TYPE_ICONS[tab.type],
        disabled: tab.disabled,
        count: tab.count,
        color: ZONING_TYPE_COLORS[tab.type],
      })),
    [allTabs, t.zoneTypes]
  )

  // Format score for display
  const formattedScore = useMemo(() => {
    if (underExploitedScore === undefined || underExploitedScore === null)
      return undefined
    return {
      value: underExploitedScore,
      maxValue: 100,
      label: t.underExploited,
      variant: getScoreVariant(underExploitedScore),
      showProgress: true,
    }
  }, [underExploitedScore, t.underExploited])

  // Loading state
  if (loading) {
    return (
      <Container>
        <AnalysisSectionHeader
          title={t.title}
          subtitle={t.sectionDescription}
          score={formattedScore}
        />
        <LoadingState>
          <Loading size="large" />
          <div>{t.loading}</div>
        </LoadingState>
      </Container>
    )
  }

  // Error state
  if (error) {
    return (
      <Container>
        <AnalysisSectionHeader
          title={t.title}
          subtitle={t.sectionDescription}
          score={formattedScore}
        />
        <ErrorState>
          <Icon name="alert-circle" size="large" />
          <div>{error || t.error}</div>
        </ErrorState>
      </Container>
    )
  }

  // No data state
  if (!data || availableTabs.length === 0) {
    return (
      <Container>
        <AnalysisSectionHeader
          title={t.title}
          subtitle={t.sectionDescription}
          score={formattedScore}
        />
        <EmptyState>
          <Icon name="info" size="large" />
          <div>{t.noData}</div>
        </EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      {/* Header */}
      <AnalysisSectionHeader
        title={t.title}
        subtitle={t.sectionDescription}
        score={formattedScore}
      />

      {/* Tabs */}
      <AnalysisTabButtonMenu
        tabs={tabs}
        activeTab={activeTab || ''}
        onTabChange={handleTabChange}
      />

      <ContentContainer>
        {activeTab && (
          <TabSubtitle>
            <TabSubtitleHeader>
              <Icon name={ZONING_TYPE_ICONS[activeTab] as any} size="small" />
              {t.zoneTypes?.[activeTab] || activeTab}
              <TabSubtitleBadge $color={ZONING_TYPE_COLORS[activeTab]}>
                {t.zonePriorityLabels?.[activeTab] || ''}
              </TabSubtitleBadge>
            </TabSubtitleHeader>
            <TabSubtitleDescription>
              {t.zoneDescriptions?.[activeTab] || ''}
            </TabSubtitleDescription>
          </TabSubtitle>
        )}

        {activeZones.length > 0 ? (
          <ZonesContainer>
            {activeZones.map((zone, index) => (
              <ZoneCard
                key={`zone-${activeTab}-${index}`}
                zone={zone}
                type={activeTab!}
                landId={landId || ''}
                landData={landData}
                plotCoveragePerc={
                  zone._id ? coverageByZoneId[zone._id] : undefined
                }
                zoningOverridesService={zoningOverridesService}
                zoningService={zoningService}
                translations={
                  {
                    unknownZone: t.unknownZone,
                    legend: t.legend,
                    metricsTable: t.metricsTable,
                    sourceLabels: t.sourceLabels,
                    status: t.status,
                    header: t.header,
                    documents: t.documents,
                    // Passed through to ReferenceViewerModal; not declared on
                    // ZoneCardTranslations so we relax the shape here.
                    referenceViewer: (t as any).referenceViewer,
                  } as any
                }
                locale={locale}
                readOnly={readOnly}
                zoneQAService={zoneQAService}
                zoneQATranslations={zoneQATranslations}
                onTranscribeVoice={onTranscribeVoice}
                onReportIncorrect={onReportIncorrect}
                onFetchEstimateStats={onFetchEstimateStats}
                onDistanceLimitChange={onDistanceLimitChange}
              />
            ))}
          </ZonesContainer>
        ) : (
          <EmptyState>
            <Icon name="info" size="medium" />
            <div>{t.noZonesForType}</div>
          </EmptyState>
        )}
      </ContentContainer>
    </Container>
  )
}
